var FastFinanceLinkStore_1;
import { __decorate, __metadata } from "tslib";
import { makeAutoObservable } from 'mobx';
import { component } from 'tsdi';
import { wrapRequest } from 'wrap-request';
import { injectTSDI } from 'ml-webclient-common/src/tsdi';
import { Api } from './core/api';
import { Config } from './core/config';
import { clearStateFromUrl, writePaymentSessionToUrl, writeStateToUrl } from './core/search-params';
const SESSION_KEY_USER_TOKEN = 'ffl_userSessionToken';
const SESSION_KEY_ACTIVE_FLOW = 'ffl_activeFlow';
const SESSION_KEY_REGION = 'ffl_finionPayRegion';
const INSTRUMENT_NOT_REQUIRED = new Set([
    'CASH',
    'BANK_TRANSFER'
]);
export function isInstrumentNotRequired(paymentMethod) {
    return INSTRUMENT_NOT_REQUIRED.has(paymentMethod);
}
export function isPaymentMethodValid(method) {
    if (!method) {
        return false;
    }
    if (isInstrumentNotRequired(method.paymentMethod)) {
        return true;
    }
    return method.paymentInstrumentStatus === 'CONFIRMED';
}
let FastFinanceLinkStore = FastFinanceLinkStore_1 = class FastFinanceLinkStore {
    currentFlow;
    currentStep = 'OVERVIEW';
    flowOrder = [];
    userSessionToken = sessionStorage.getItem(SESSION_KEY_USER_TOKEN) ?? undefined;
    userSessionTokenValidUntil;
    debtClaimIds;
    errorMessage;
    finionPayRegion = sessionStorage.getItem(SESSION_KEY_REGION) ?? undefined;
    isPollingPaymentStatus = false;
    // Route params stored here
    _tenant;
    _token;
    _client;
    _flowParam;
    _activeFlowParam;
    _activeStepParam;
    _isRedirect = false;
    get config() {
        return injectTSDI(Config);
    }
    get api() {
        return injectTSDI(Api);
    }
    getFastFinanceLinkRequest = wrapRequest((token) => this.api.client.GetFastFinanceLink.getFastFinanceLink(token));
    getAccountBalanceRequest = wrapRequest(({ token, debtClaimIds }) => this.api.client.GetAccountBalance.getAccountBalance(token, {
        ...(debtClaimIds?.length && { debtClaimIds })
    }));
    createUserPaymentSessionRequest = wrapRequest(({ token, debtClaimIds, dto }) => this.api.client.CreateUserPaymentSession.createUserPaymentSession(token, { ...(debtClaimIds?.length && { debtClaimIds }) }, dto));
    balanceCustomerAccountRequest = wrapRequest(({ token, dto }) => this.api.client.BalanceCustomerAccount.balanceCustomerAccount(token, dto));
    updateCustomerPaymentMethodRequest = wrapRequest(({ token, paymentRequestToken }) => this.api.client.UpdateCustomerPaymentMethod.updateCustomerPaymentMethod(token, paymentRequestToken));
    getPaymentStatusRequest = wrapRequest(({ token, paymentRequestToken }) => this.api.client.GetPaymentStatus.getPaymentStatus(token, paymentRequestToken));
    get tenant() {
        return this._tenant;
    }
    get token() {
        return this._token;
    }
    get client() {
        return this._client;
    }
    get fastFinanceLinkData() {
        return this.getFastFinanceLinkRequest.result;
    }
    get accountBalance() {
        return this.getAccountBalanceRequest.result;
    }
    get bookingEntries() {
        return this.accountBalance?.customerAccountDto?.bookingEntries ?? [];
    }
    get totalAmount() {
        return this.accountBalance?.customerAccountDto?.openAmount;
    }
    get paymentFee() {
        return this.accountBalance?.customerAccountPaymentFeeDto?.fee;
    }
    get totalWithFee() {
        return this.accountBalance?.customerAccountPaymentFeeDto?.totalPrice;
    }
    get studioInfo() {
        return this.fastFinanceLinkData?.masterData?.publicData;
    }
    get studioLogo() {
        return this.fastFinanceLinkData?.masterData?.logos?.studioLogoUrl;
    }
    get internalData() {
        return this.fastFinanceLinkData?.masterData?.internalData;
    }
    get legal() {
        return this.fastFinanceLinkData?.masterData?.legal;
    }
    get currentPaymentMethod() {
        return this.fastFinanceLinkData?.customerPaymentMethodDto;
    }
    get allowedPaymentChoices() {
        return (this.fastFinanceLinkData?.customerSelfServiceConfigurationDto
            ?.allowedPaymentChoices ?? []);
    }
    get customerInfo() {
        return this.fastFinanceLinkData?.customerSelfServiceConfigurationDto;
    }
    get styling() {
        return this.fastFinanceLinkData?.styling;
    }
    get customerNumber() {
        return this.customerInfo?.customerNumber;
    }
    get contracts() {
        return this.customerInfo?.contracts ?? [];
    }
    /**
     * Returns debt claim IDs for payment.
     * Uses URL debtClaimIds if specified, otherwise all booking entry IDs.
     */
    get debtClaimIdsForPayment() {
        return (this.debtClaimIds ?? this.bookingEntries.map((e) => e.databaseId));
    }
    /**
     * Returns true when processing payment after onPaymentSuccess callback.
     * Used to show "Processing payment..." / "Updating payment method..." loader.
     */
    get isProcessingPayment() {
        return (this.balanceCustomerAccountRequest.loading ||
            this.updateCustomerPaymentMethodRequest.loading ||
            this.isPollingPaymentStatus);
    }
    get isLoading() {
        return (this.getFastFinanceLinkRequest.loading ||
            this.getAccountBalanceRequest.loading ||
            this.createUserPaymentSessionRequest.loading ||
            this.balanceCustomerAccountRequest.loading ||
            this.updateCustomerPaymentMethodRequest.loading ||
            this.getPaymentStatusRequest.loading);
    }
    get initError() {
        return (this.getFastFinanceLinkRequest.error ??
            this.getAccountBalanceRequest.error);
    }
    get hasError() {
        return Boolean(this.initError) || Boolean(this.errorMessage);
    }
    get isInitialized() {
        if (this.errorMessage) {
            return true;
        }
        if (this.getFastFinanceLinkRequest.error) {
            return true;
        }
        if (!this.getFastFinanceLinkRequest.fetched) {
            return false;
        }
        if (this.getAccountBalanceRequest.loading) {
            return false;
        }
        return true;
    }
    get hasOpenClaims() {
        return this.bookingEntries.length > 0;
    }
    get currentFlowIndex() {
        if (!this.currentFlow) {
            return -1;
        }
        return this.flowOrder.indexOf(this.currentFlow);
    }
    get isLastFlow() {
        return (this.currentFlowIndex < 0 ||
            this.currentFlowIndex >= this.flowOrder.length - 1);
    }
    get nextFlow() {
        const nextIndex = this.currentFlowIndex + 1;
        return nextIndex > 0 && nextIndex < this.flowOrder.length
            ? this.flowOrder[nextIndex]
            : undefined;
    }
    get isPaymentMethodUpdateBeforePayment() {
        const pmuIndex = this.flowOrder.indexOf('PAYMENT_METHOD_UPDATE');
        const payIndex = this.flowOrder.indexOf('PAYMENT');
        return pmuIndex !== -1 && payIndex !== -1 && pmuIndex < payIndex;
    }
    get hasValidExistingPaymentMethod() {
        return isPaymentMethodValid(this.currentPaymentMethod);
    }
    get canSkipPaymentMethodUpdate() {
        return (this.isPaymentMethodUpdateBeforePayment &&
            this.hasValidExistingPaymentMethod);
    }
    get countryCode() {
        return (this.studioInfo?.address?.country ?? this.config.defaultCountryCode);
    }
    get hasAccessRestriction() {
        return this.customerInfo?.accessRestricted ?? false;
    }
    get dunningLevel() {
        return this.customerInfo?.dunningLevel ?? 0;
    }
    get scenarioConfig() {
        return {
            openAmount: this.totalAmount?.amount ?? 0,
            dunningLevel: this.dunningLevel,
            accessRestriction: this.hasAccessRestriction,
            validPaymentMethod: this.hasValidExistingPaymentMethod
        };
    }
    constructor() {
        makeAutoObservable(this);
    }
    /**
     * Initialize the store with route params.
     * @param isRedirect - true when the page loaded with PSP redirect params (paymentToken/redirectResult)
     */
    initWithParams(params, isRedirect) {
        this._tenant = params.t;
        this._token = params.token;
        this._client = params.c;
        this._flowParam = params.flow;
        this._activeFlowParam = params.activeFlow;
        this._activeStepParam = params.activeStep;
        this.debtClaimIds = params.debtClaimIds;
        this._isRedirect = isRedirect;
        if (params.ust) {
            this.userSessionToken = params.ust;
            this.finionPayRegion = params.fpRegion ?? undefined;
            this.persistSessionToStorage(params.ust, params.fpRegion);
        }
        this.api.setEndpoint(this.config.getApiEndpoint(this._tenant, this._client));
        void this.loadInitialData();
    }
    setFlow = (flow) => {
        this.currentFlow = flow;
        this.currentStep = 'OVERVIEW';
        this.syncUrlState();
    };
    async createPaymentSession(scope) {
        if (!this._token) {
            throw new Error('No token found');
        }
        const amount = scope === 'ECOM' ? (this.totalWithFee?.amount ?? 0) : 0;
        const response = await this.createUserPaymentSessionRequest.request({
            token: this._token,
            debtClaimIds: this.debtClaimIds,
            dto: {
                amount,
                paymentSessionScope: scope
            }
        });
        if (!response) {
            const { error } = this.createUserPaymentSessionRequest;
            throw new Error(error?.message ?? 'Failed to create payment session');
        }
        this.userSessionToken = response.token;
        this.userSessionTokenValidUntil = response.tokenValidUntil?.toDate();
        this.finionPayRegion = response.finionPayRegion;
        if (response.token) {
            this.persistSessionToStorage(response.token, response.finionPayRegion);
            writePaymentSessionToUrl(response.token, response.finionPayRegion);
        }
        return response;
    }
    async handlePaymentSuccess(paymentRequestToken) {
        if (!this._token) {
            throw new Error('No token found');
        }
        try {
            if (this.currentFlow === 'PAYMENT') {
                const result = await this.balanceCustomerAccountRequest.request({
                    token: this._token,
                    dto: {
                        debtClaimsPayments: this.debtClaimIdsForPayment,
                        paymentRequestToken
                    }
                });
                if (!result) {
                    const { error } = this.balanceCustomerAccountRequest;
                    throw new Error(error?.message ?? 'Failed to process payment');
                }
                await this.pollPaymentStatus(paymentRequestToken);
            }
            else if (this.currentFlow === 'PAYMENT_METHOD_UPDATE') {
                await this.updateCustomerPaymentMethodRequest.request({
                    token: this._token,
                    paymentRequestToken
                });
                if (this.updateCustomerPaymentMethodRequest.error) {
                    throw new Error(this.updateCustomerPaymentMethodRequest.error.message ??
                        'Failed to update payment method');
                }
            }
            this.currentStep = 'SUCCESS';
            this.clearSessionStorage();
            clearStateFromUrl();
        }
        catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : 'Payment failed';
            this.currentStep = 'ERROR';
            this.clearSessionStorage();
            clearStateFromUrl();
        }
    }
    handlePaymentError = (error) => {
        console.error('Payment error:', error);
        this.errorMessage = error.message;
        this.currentStep = 'ERROR';
        this.clearSessionStorage();
        clearStateFromUrl();
    };
    proceedToNextFlow = () => {
        const next = this.nextFlow;
        if (!next) {
            return;
        }
        this.currentFlow = next;
        this.currentStep = 'OVERVIEW';
        this.userSessionToken = undefined;
        this.errorMessage = undefined;
        this.clearSessionStorage();
        this.syncUrlState();
    };
    skipPaymentMethodUpdate = () => {
        if (!this.canSkipPaymentMethodUpdate) {
            return;
        }
        this.currentFlow = 'PAYMENT';
        this.currentStep = 'OVERVIEW';
        this.userSessionToken = undefined;
        this.errorMessage = undefined;
        this.clearSessionStorage();
        this.syncUrlState();
    };
    proceedToAction = () => {
        this.currentStep = 'ACTION';
        if (this.currentFlow) {
            sessionStorage.setItem(SESSION_KEY_ACTIVE_FLOW, this.currentFlow);
        }
        this.syncUrlState();
    };
    goBackToOverview = () => {
        this.currentStep = 'OVERVIEW';
        this.errorMessage = undefined;
        this.syncUrlState();
    };
    async refreshAccountBalance() {
        if (!this._token) {
            return;
        }
        await this.getAccountBalanceRequest.request({
            token: this._token,
            debtClaimIds: this.debtClaimIds
        });
    }
    async pollPaymentStatus(paymentRequestToken) {
        const INTERVAL_MS = 2000;
        const MAX_ATTEMPTS = 15; // 30s total
        this.isPollingPaymentStatus = true;
        try {
            for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
                const result = await this.getPaymentStatusRequest.request({
                    token: this._token,
                    paymentRequestToken
                });
                if (this.getPaymentStatusRequest.error) {
                    throw new Error(this.getPaymentStatusRequest.error.message ??
                        'Failed to fetch payment status');
                }
                if (result?.status === 'SUCCESSFUL') {
                    return;
                }
                if (result?.status === 'FAILED') {
                    throw new Error('Payment failed');
                }
                if (attempt < MAX_ATTEMPTS - 1) {
                    await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
                }
            }
            throw new Error('Payment status did not reach SUCCESSFUL within the expected time');
        }
        finally {
            this.isPollingPaymentStatus = false;
        }
    }
    persistSessionToStorage(token, region) {
        sessionStorage.setItem(SESSION_KEY_USER_TOKEN, token);
        if (region) {
            sessionStorage.setItem(SESSION_KEY_REGION, region);
        }
        else {
            sessionStorage.removeItem(SESSION_KEY_REGION);
        }
    }
    clearSessionStorage() {
        sessionStorage.removeItem(SESSION_KEY_USER_TOKEN);
        sessionStorage.removeItem(SESSION_KEY_ACTIVE_FLOW);
        sessionStorage.removeItem(SESSION_KEY_REGION);
    }
    static flowEnumToParam(flow) {
        return flow === 'PAYMENT' ? 'payment' : 'payment_method';
    }
    static flowParamToEnum(param) {
        return param === 'payment' ? 'PAYMENT' : 'PAYMENT_METHOD_UPDATE';
    }
    static stepEnumToParam(step) {
        return step === 'ACTION' ? 'action' : 'overview';
    }
    syncUrlState() {
        if (!this.currentFlow) {
            return;
        }
        writeStateToUrl(FastFinanceLinkStore_1.flowEnumToParam(this.currentFlow), FastFinanceLinkStore_1.stepEnumToParam(this.currentStep));
    }
    initializeFlowOrder(serverFlowOrder) {
        if (this._flowParam === 'payment') {
            this.flowOrder = ['PAYMENT'];
            this.currentFlow = 'PAYMENT';
        }
        else if (this._flowParam === 'payment_method') {
            this.flowOrder = ['PAYMENT_METHOD_UPDATE'];
            this.currentFlow = 'PAYMENT_METHOD_UPDATE';
        }
        else {
            this.flowOrder = [...serverFlowOrder];
            [this.currentFlow] = this.flowOrder;
        }
    }
    skipPaymentFlowIfNoDebts() {
        if (this.bookingEntries.length === 0 && this.flowOrder.length > 1) {
            this.flowOrder = this.flowOrder.filter((f) => f !== 'PAYMENT');
            [this.currentFlow] = this.flowOrder;
        }
    }
    restoreState() {
        if (this._activeFlowParam) {
            const flowEnum = FastFinanceLinkStore_1.flowParamToEnum(this._activeFlowParam);
            if (this.flowOrder.includes(flowEnum)) {
                this.currentFlow = flowEnum;
            }
        }
        else if (this._isRedirect) {
            const savedFlow = sessionStorage.getItem(SESSION_KEY_ACTIVE_FLOW);
            if (savedFlow && this.flowOrder.includes(savedFlow)) {
                this.currentFlow = savedFlow;
            }
        }
        if (this.currentFlow === 'PAYMENT' &&
            this.isPaymentMethodUpdateBeforePayment &&
            !this.hasValidExistingPaymentMethod) {
            this.currentFlow = 'PAYMENT_METHOD_UPDATE';
            this.currentStep = 'OVERVIEW';
            return;
        }
        if (this._isRedirect) {
            this.currentStep = 'ACTION';
        }
        else if (this._activeStepParam === 'action') {
            this.currentStep = 'ACTION';
        }
    }
    async loadInitialData() {
        if (!this._tenant) {
            this.errorMessage = 'Missing tenant parameter (t)';
            return;
        }
        if (!this._token) {
            this.errorMessage = 'Missing token parameter';
            return;
        }
        const data = await this.getFastFinanceLinkRequest.request(this._token);
        if (this.getFastFinanceLinkRequest.error || !data) {
            return;
        }
        const config = data.customerSelfServiceConfigurationDto;
        if (!config) {
            return;
        }
        this.initializeFlowOrder(config.flowOrder);
        if (this.flowOrder.includes('PAYMENT')) {
            await this.getAccountBalanceRequest.request({
                token: this._token,
                debtClaimIds: this.debtClaimIds
            });
            if (this.getAccountBalanceRequest.error) {
                return;
            }
            this.skipPaymentFlowIfNoDebts();
        }
        // Restore flow/step from URL params or sessionStorage
        if (this._activeFlowParam ||
            this._activeStepParam ||
            this._isRedirect) {
            this.restoreState();
        }
    }
};
FastFinanceLinkStore = FastFinanceLinkStore_1 = __decorate([
    component(),
    __metadata("design:paramtypes", [])
], FastFinanceLinkStore);
export { FastFinanceLinkStore };
